import { Mirroring } from '../../interfaces/cartridge';
import { BaseCartridge, RomImage } from './base';

/**
 * Mapper 1 — MMC1.
 *
 * Serial 5-bit shift register at 0x8000-0xFFFF: bit 0 of each write shifts in,
 * bit 7 resets. After 5 writes the value commits to the register selected by
 * address bits 13-14:
 *   0x8000-0x9FFF control: MM P P C  (mirroring, PRG mode, CHR mode)
 *   0xA000-0xBFFF CHR bank 0 (4KB; low bit ignored in 8KB mode)
 *   0xC000-0xDFFF CHR bank 1 (4KB; 4KB mode only)
 *   0xE000-0xFFFF PRG bank (16KB; bit 4 = PRG-RAM enable, active low)
 *
 * PRG modes: 0/1 = 32KB switch at 0x8000; 2 = fix first at 0x8000, switch
 * 0xC000; 3 = switch 0x8000, fix last at 0xC000.
 *
 * NOTE: the consecutive-cycle write ignore of real MMC1 hardware is NOT
 * emulated (games don't rely on it; would need CPU-cycle knowledge).
 */
export class Mmc1Cartridge extends BaseCartridge {
    private shift = 0x10; // bit 4 set = "1 write so far" sentinel position
    private control = 0x0C;
    private chrBank0 = 0;
    private chrBank1 = 0;
    private prgBank = 0;
    private prgRamEnabled = true;

    constructor(image: RomImage) {
        super(image);
    }

    cpuRead(address: number): number {
        const ram = { value: 0 };
        if (this.prgRamEnabled && this.readPrgRam(address, ram)) return ram.value;
        if (address >= 0x8000) {
            const prgMode = (this.control >> 2) & 0x03;
            const banks16 = Math.max(1, this.prgRom.length / 0x4000);
            if (prgMode < 2) {
                // 32KB mode: consecutive 16KB pair at 0x8000 (4 x 8KB slots)
                const slot = (address - 0x8000) >> 13;
                return this.readPrg8((this.prgBank & 0x0E) * 2 + slot, address);
            }
            if (address < 0xC000) {
                return this.readPrg16(prgMode === 2 ? 0 : this.prgBank, address);
            }
            return this.readPrg16(prgMode === 2 ? this.prgBank : banks16 - 1, address);
        }
        return 0;
    }

    cpuWrite(address: number, value: number): void {
        if (this.prgRamEnabled && this.writePrgRam(address, value)) return;
        if (address < 0x8000) return;

        if (value & 0x80) {
            this.shift = 0x10;
            this.control |= 0x0C; // PRG mode 3
            return;
        }
        const complete = (this.shift & 1) !== 0;
        this.shift = (this.shift >> 1) | ((value & 1) << 4);
        if (!complete) return;

        const reg = this.shift & 0x1F;
        this.shift = 0x10;
        const region = (address >> 13) & 0x03;
        switch (region) {
            case 0: {
                this.control = reg;
                const mm = reg & 0x03;
                this.mirroring = (['single-screen-0', 'single-screen-1', 'vertical', 'horizontal'] as Mirroring[])[mm];
                break;
            }
            case 1:
                this.chrBank0 = reg;
                break;
            case 2:
                this.chrBank1 = reg;
                break;
            case 3:
                this.prgBank = reg & 0x0F;
                this.prgRamEnabled = (reg & 0x10) === 0;
                break;
        }
    }

    private chrOffset(address: number): number {
        const chrMode = (this.control >> 4) & 1;
        const banks4 = Math.max(1, this.chr.length / 0x1000);
        if (chrMode === 0) {
            // One 8KB bank (low bit of chrBank0 ignored)
            const banks8 = Math.max(1, this.chr.length / 0x2000);
            const b = ((this.chrBank0 >> 1) % banks8) * 0x2000;
            return (b + (address & 0x1FFF)) % this.chr.length;
        }
        const bank = address < 0x1000 ? this.chrBank0 : this.chrBank1;
        const b = (bank % banks4) * 0x1000;
        return (b + (address & 0x0FFF)) % this.chr.length;
    }

    ppuRead(address: number): number {
        return this.chr[this.chrOffset(address & 0x1FFF)];
    }

    ppuWrite(address: number, value: number): void {
        if (this.chrIsRam) {
            this.chr[this.chrOffset(address & 0x1FFF)] = value & 0xFF;
        }
    }
}
